<template>
  <WdsButtonGhost @click="openPanel">
    <WdsStack align-center :data-test="`tc-name-${index}`">
      <WdsAvatar
        sm
        :path="avatarPath"
        :class="{
          'tw-opacity-40': column.managedStatus === TrustedContactManagedStatus.Unmanaged,
        }"
      />

      <WdsStack direction-column class="tw-ml-2.5 lg:tw-max-w-[200px] xl:tw-max-w-none">
        <WdsText>
          <template #default>
            <WorkerName
              :name="worker.name"
              :manages-workers="worker.hiresWithAttribution?.[0]?.recruiterManagesWorkers ?? false"
              :managed-status="column.managedStatus"
              :recruiter-name="worker.hiresWithAttribution?.[0]?.recruiter?.name"
              :recruiter-fees="worker.hiresWithAttribution?.[0]?.fees ?? []"
              :recruiter-ownership-start-date="
                worker.hiresWithAttribution?.[0]?.recruiterOwnershipStartDate
              "
              :recruiter-ownership-days="worker.hiresWithAttribution?.[0]?.recruiterOwnershipDays"
              :recruiter-ownership-days-left="
                worker.hiresWithAttribution?.[0]?.recruiterOwnershipDaysLeft
              "
            />

            <WdsStack direction-row gap-sm>
              <WdsLabel v-if="joinedRecently" subtle xs success>
                {{ $t('New') }}
              </WdsLabel>

              <TrustedContactStatusLabel
                v-if="column.status"
                :status="column.status"
                :data-test="`tc-status-${index}`"
                xs
              />

              <WdsLabel
                v-if="worker.isCurrentlyHired"
                subtle
                xs
                primary
                class="tw-text-pretty tw-break-normal"
                :data-test="`tc-hire-status-${index}`"
              >
                {{ $t('Currently hired') }}
              </WdsLabel>

              <WdsLabel
                v-if="column.managedStatus === TrustedContactManagedStatus.StaffingAgency"
                subtle
                xs
                primary
                class="tw-text-pretty tw-break-normal"
                :data-test="`tc-managed-${index}`"
              >
                {{ $t('Managed') }}
              </WdsLabel>
            </WdsStack>
          </template>

          <template #subText>
            <div class="tw-max-w-[250px] tw-truncate tw-text-left">
              <span
                :class="{
                  'tw-text-gray-600':
                    column.managedStatus === TrustedContactManagedStatus.Unmanaged,
                }"
                :title="(worker.jobTitle ?? '').trim()"
              >
                {{ (worker.jobTitle ?? '').trim() }}
              </span>
            </div>
          </template>
        </WdsText>
      </WdsStack>
    </WdsStack>
  </WdsButtonGhost>
</template>

<script lang="ts" setup>
import { computed, inject } from 'vue'
import { WdsAvatar, WdsButtonGhost, WdsLabel, WdsStack, WdsText } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { getAvatarPath } from '@/utils/avatar'
import { useTablePanels } from '@/components/GraphqlDataTable/useTablePanels'
import { TableInModal } from '@/components/GraphqlDataTable/injection'
import { TrustedContactManagedStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import WorkerName from '@/components/WorkerName.vue'
import TrustedContactStatusLabel from '@/components/Contacts/CompanyFreelancer/TrustedContactStatusLabel.vue'

const { openWorkerDetailPanel } = useTablePanels()

const inModal = inject(TableInModal, false)

// Slim variant for surfaces that don't want the contact-status / currently-hired
// labels (e.g. the compliance tracker, where the dedicated status column already
// communicates review state). Registered separately in TrustedContacts/index.ts;
// the component handles both shapes via v-if guards on the optional fields.
//
// Codegen emits the wide fragment's TS type from its own selection set only
// (fragment spreads don't merge fields into the parent type), so we list the
// shared selections in both fragments rather than spreading.
const TrustedContactWorkerColumnCompact = graphql(`
  fragment TrustedContactWorkerColumnCompact on TrustedContact {
    id
    managedStatus
    createdAt
    worker {
      id
      name
      initials
      avatar
      jobTitle
      hiresWithAttribution {
        id
        recruiter {
          id
          name
        }
        fees {
          ...FeeFragment
        }
        recruiterManagesWorkers
        recruiterOwnershipStartDate
        recruiterOwnershipDays
        recruiterOwnershipDaysLeft
      }
    }
  }
`)

const TrustedContactWorkerColumn = graphql(`
  fragment TrustedContactWorkerColumn on TrustedContact {
    id
    status
    managedStatus
    createdAt
    worker {
      id
      name
      initials
      avatar
      jobTitle
      isCurrentlyHired(account: $companyId)
      viewerCanOfferJob(account: $companyId)
      hiresWithAttribution {
        recruiter {
          name
        }
        fees {
          ...FeeFragment
        }
        recruiterManagesWorkers
        recruiterOwnershipStartDate
        recruiterOwnershipDays
        recruiterOwnershipDaysLeft
      }
    }
  }
`)

const props = defineProps<{
  row:
    | FragmentType<typeof TrustedContactWorkerColumn>
    | FragmentType<typeof TrustedContactWorkerColumnCompact>
  index: number
}>()
// Cast through the wide fragment so optional fields are typed; runtime values
// will be undefined when the parent registered the compact variant.
const column = computed(() =>
  useFragment(TrustedContactWorkerColumn, props.row as FragmentType<typeof TrustedContactWorkerColumn>),
)
const worker = computed(() => column.value.worker)

const avatarPath = computed(() => getAvatarPath(worker.value.avatar, worker.value.id))

const joinedRecently = computed(() => {
  const fourteenDaysAgo = new Date()
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)
  return new Date(column.value.createdAt) >= fourteenDaysAgo
})

const openPanel = () => {
  if (!inModal) {
    openWorkerDetailPanel({
      workerAccountId: worker.value.id,
    })
  }
}
</script>

<style scoped></style>
